import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { track } from "../lib/analytics";
import AppLayout from "../components/app/AppLayout";
import { ShoppingCart, Plus, Clock, MapPin, Hash, Send, Filter, X, Cpu, Search } from "lucide-react";

const MACHINE_OPT_KEYS = [
  "", "vertical_machining_center", "horizontal_machining_center", "5_axis_machining_center",
  "cnc_lathe", "cnc_lathe_with_live_tooling", "multitasking_mill_turn", "swiss_lathe",
  "surface_grinder", "cylindrical_grinder", "wire_edm", "sinker_edm",
  "fiber_laser", "press_brake", "waterjet",
];
const TOLERANCE_OPT_KEYS = ["", "rough", "standard", "precision", "ultra_precision"];
const COUNTRY_OPT_KEYS = ["", "IT", "DE", "FR", "ES", "PL", "CH"];
const SORT_OPT_KEYS = ["newest", "oldest", "qty_desc", "qty_asc", "budget_desc", "deadline_asc"];

const EMPTY_FILTERS = {
  q: "", country: "", city: "", material: "",
  machine_category: "", tolerance_class: "",
  qty_min: "", qty_max: "", budget_min: "", budget_max: "",
  deadline_before: "", sort: "newest",
};

export default function MarketplacePage() {
  const { t } = useTranslation();
  const MACHINE_OPTIONS = useMemo(() => MACHINE_OPT_KEYS.map((k) => ({ v: k, label: k === "" ? t("marketplace.machineOpts.any") : t(`marketplace.machineOpts.${k}`) })), [t]);
  const TOLERANCE_OPTIONS = useMemo(() => TOLERANCE_OPT_KEYS.map((k) => ({ v: k, label: k === "" ? t("marketplace.toleranceOpts.any") : t(`marketplace.toleranceOpts.${k}`) })), [t]);
  const COUNTRY_OPTIONS = useMemo(() => COUNTRY_OPT_KEYS.map((k) => ({ v: k, label: k === "" ? t("marketplace.countryOpts.all") : t(`countries.${k}`) })), [t]);
  const SORT_OPTIONS = useMemo(() => SORT_OPT_KEYS.map((k) => ({ v: k, label: t(`marketplace.sort.${k}`) })), [t]);
  const COUNTRY_OPTIONS_NON_EMPTY = useMemo(() => COUNTRY_OPTIONS.filter((c) => c.v), [COUNTRY_OPTIONS]);

  const [tab, setTab] = useState("open");
  const [rfqs, setRfqs] = useState([]);
  const [mineRfqs, setMineRfqs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", quantity: 1, material: "", operations: "",
    machine_category: "", tolerance_class: "",
    deadline: "", location_city: "", location_country: "IT", budget_max_eur: "",
  });
  const [selected, setSelected] = useState(null);
  const [offer, setOffer] = useState({ price_eur: "", lead_time_days: "", notes: "" });
  // Back-pointer to the quote that spawned this RFQ (when prefilled via the
  // OutsourceBanner CTA). Sent on create so QuoteDetailPage can later show
  // "X offers received" inline.
  const [linkedQuoteId, setLinkedQuoteId] = useState(null);

  // Filters
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [onlyCompatible, setOnlyCompatible] = useState(false);
  const [userTier, setUserTier] = useState("free");
  const [compatibleError, setCompatibleError] = useState("");

  useEffect(() => {
    api.get("/me/tier").then((r) => setUserTier(r.data?.tier || "free")).catch(() => {});
  }, []);

  // Prefill RFQ form from a quote (?prefill={quote_id}&action=create), used by
  // the QuoteDetailPage OutsourceBanner to convert a non-compatible quote
  // into a marketplace request with one click.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const prefillId = searchParams.get("prefill");
    const action = searchParams.get("action");
    if (!prefillId) return;
    let cancelled = false;
    api.get(`/quotes/${prefillId}`).then((r) => {
      if (cancelled) return;
      const q = r.data || {};
      const a = q.analysis || {};
      const c = q.calculation || {};
      const breakdown = c.breakdown || [];
      const outsourceRows = breakdown.filter((row) => row.outsource === true || row.owned === false);
      const opsList = outsourceRows
        .map((row) => row.operation_label || row.operation)
        .filter(Boolean)
        .join(", ");
      const partDesc = a.part_description || q.material_name || "Pezzo";
      const descLines = [
        `Pezzo: ${partDesc}`,
        q.material_name ? `Materiale: ${q.material_name}` : null,
        a.tolerance_class ? `Tolleranza: ${a.tolerance_class}` : null,
        a.surface_finish_ra_um ? `Finitura Ra: ${a.surface_finish_ra_um} µm` : null,
        opsList ? `Operazioni richieste: ${opsList}` : null,
        a?.suggested_machine?.category_label ? `Macchina suggerita AI: ${a.suggested_machine.category_label}` : null,
      ].filter(Boolean);
      setForm((prev) => ({
        ...prev,
        title: `RFQ — ${partDesc.slice(0, 60)}`,
        description: descLines.join("\n"),
        quantity: q.batch_size || prev.quantity,
        material: q.material_name || prev.material,
        operations: opsList,
        machine_category: a?.suggested_machine?.category || prev.machine_category,
        tolerance_class: a?.tolerance_class || prev.tolerance_class,
      }));
      setLinkedQuoteId(prefillId);
      if (action === "create") setCreating(true);
      // Clean the URL so a refresh doesn't re-trigger the prefill.
      setSearchParams({}, { replace: true });
    }).catch(() => { /* silent — leave the form blank if quote not found */ });
    return () => { cancelled = true; };
    // We intentionally only run on the initial mount (or when query changes).
  }, [searchParams, setSearchParams]);

  const isProPlus = userTier === "pro_plus" || userTier === "premium";

  const buildParams = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p[k] = v;
    });
    if (onlyCompatible && isProPlus) p.only_compatible = true;
    return p;
  }, [filters, onlyCompatible, isProPlus]);

  const load = useCallback(async () => {
    setCompatibleError("");
    const params = buildParams();
    try {
      const [a, b] = await Promise.all([
        api.get("/marketplace/rfqs", { params }),
        api.get("/marketplace/rfqs", { params: { ...params, mine: true, only_compatible: undefined } }),
      ]);
      setRfqs(a.data || []); setMineRfqs(b.data || []);
    } catch (e) {
      if (e.response?.status === 403) {
        setCompatibleError(e.response.data?.detail || "Pro+ richiesto");
        setOnlyCompatible(false);
      }
    }
  }, [buildParams]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        budget_max_eur: form.budget_max_eur ? parseFloat(form.budget_max_eur) : null,
        operations: form.operations.split(",").map((s) => s.trim()).filter(Boolean),
        linked_quote_id: linkedQuoteId || undefined,
      };
      // Strip empty optionals
      ["machine_category", "tolerance_class", "deadline", "location_city", "location_country", "material"]
        .forEach((k) => { if (!payload[k]) delete payload[k]; });
      await api.post("/marketplace/rfqs", payload);
      track("marketplace_rfq_created", {
        machine_category: payload.machine_category || null,
        tolerance_class: payload.tolerance_class || null,
        quantity: payload.quantity,
        has_budget: !!payload.budget_max_eur,
        from_quote: !!linkedQuoteId,
      });
      setCreating(false);
      setForm({ title: "", description: "", quantity: 1, material: "", operations: "", machine_category: "", tolerance_class: "", deadline: "", location_city: "", location_country: "IT", budget_max_eur: "" });
      setLinkedQuoteId(null);
      load();
    } catch (e2) { alert(e2.response?.data?.detail || "Errore"); }
  };

  const openRfq = async (id) => { const { data } = await api.get(`/marketplace/rfqs/${id}`); setSelected(data); };

  const submitOffer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/marketplace/offers", { rfq_id: selected.id, price_eur: parseFloat(offer.price_eur), lead_time_days: parseInt(offer.lead_time_days) || 0, notes: offer.notes });
      setOffer({ price_eur: "", lead_time_days: "", notes: "" });
      openRfq(selected.id);
    } catch (e2) { alert(e2.response?.data?.detail || "Errore"); }
  };

  const closeRfq = async (id) => {
    if (!window.confirm(t("marketplace.closeRfqConfirm"))) return;
    await api.delete(`/marketplace/rfqs/${id}`); load();
  };

  const list = tab === "open" ? rfqs : mineRfqs;
  const activeFilters = Object.entries(filters).filter(([k, v]) => v !== "" && k !== "sort").length;
  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-7xl mx-auto" data-testid="marketplace-page">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="label-tech">{t("marketplace.label")}</span>
              <span className="text-[10px] label-tech px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/40">{t("marketplace.free")}</span>
            </div>
            <h1 className="font-display font-bold text-3xl text-slate-100 flex items-center gap-3"><ShoppingCart size={26} className="text-blue-400"/> {t("marketplace.title")}</h1>
          </div>
          <button onClick={() => setCreating(!creating)} className="btn-primary px-5 py-3 rounded-md flex items-center gap-2" data-testid="new-rfq-btn">
            <Plus size={16}/> {t("marketplace.publishRfq")}
          </button>
        </div>
        <p className="text-sm text-slate-400 mb-6">{t("marketplace.subtitle")}</p>

        {creating && (
          <form onSubmit={create} className="tech-card p-6 mb-6 fade-in-up" data-testid="rfq-form">
            <div className="font-display font-semibold text-slate-100 mb-4">{t("marketplace.newRfqForm")}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2"><L>{t("marketplace.title_field")} *</L><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-4 py-3 rounded-md text-sm" data-testid="rfq-title"/></div>
              <div><L>{t("marketplace.quantity")} *</L><input type="number" min="1" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="w-full px-4 py-3 rounded-md text-sm" data-testid="rfq-quantity"/></div>
              <div className="md:col-span-3"><L>{t("marketplace.description")} *</L><textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 rounded-md text-sm" data-testid="rfq-description"/></div>
              <div><L>{t("marketplace.material")}</L><input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} className="w-full px-4 py-3 rounded-md text-sm" placeholder={t("marketplace.materialPh")}/></div>
              <div>
                <L>{t("marketplace.machineCategory")}</L>
                <select value={form.machine_category} onChange={(e) => setForm({ ...form, machine_category: e.target.value })} className="w-full px-3 py-3 rounded-md text-sm" data-testid="rfq-machine">
                  {MACHINE_OPTIONS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <L>{t("marketplace.toleranceRequired")}</L>
                <select value={form.tolerance_class} onChange={(e) => setForm({ ...form, tolerance_class: e.target.value })} className="w-full px-3 py-3 rounded-md text-sm" data-testid="rfq-tolerance">
                  {TOLERANCE_OPTIONS.map((tl) => <option key={tl.v} value={tl.v}>{tl.label}</option>)}
                </select>
              </div>
              <div><L>{t("marketplace.operations")}</L><input value={form.operations} onChange={(e) => setForm({ ...form, operations: e.target.value })} className="w-full px-4 py-3 rounded-md text-sm" placeholder={t("marketplace.operationsPlaceholder")}/></div>
              <div>
                <L>{t("marketplace.country")}</L>
                <select value={form.location_country} onChange={(e) => setForm({ ...form, location_country: e.target.value })} className="w-full px-3 py-3 rounded-md text-sm" data-testid="rfq-country">
                  {COUNTRY_OPTIONS_NON_EMPTY.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                </select>
              </div>
              <div><L>{t("marketplace.city")}</L><input value={form.location_city} onChange={(e) => setForm({ ...form, location_city: e.target.value })} className="w-full px-4 py-3 rounded-md text-sm" placeholder="Milano" data-testid="rfq-city"/></div>
              <div><L>{t("marketplace.deadline")}</L><input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="w-full px-4 py-3 rounded-md text-sm"/></div>
              <div><L>{t("marketplace.budgetMax")}</L><input type="number" value={form.budget_max_eur} onChange={(e) => setForm({ ...form, budget_max_eur: e.target.value })} className="w-full px-4 py-3 rounded-md text-sm"/></div>
            </div>
            <div className="mt-5 flex gap-3">
              <button type="submit" className="btn-primary px-4 py-2 rounded-md flex items-center gap-2" data-testid="rfq-submit"><Send size={14}/> {t("marketplace.publish")}</button>
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-md text-sm bg-slate-800 text-slate-300 border border-slate-700">{t("common.cancel")}</button>
            </div>
          </form>
        )}

        {/* Filter bar */}
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="marketplace-filters">
          {tab === "open" && (
            <button
              onClick={() => {
                if (!isProPlus) { setCompatibleError(t("marketplace.matchProPlusRequired")); return; }
                setOnlyCompatible((s) => !s); setCompatibleError("");
              }}
              className={`px-3 py-2 rounded-md text-sm border flex items-center gap-2 transition-colors ${
                onlyCompatible
                  ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/50"
                  : isProPlus
                    ? "bg-slate-900 text-slate-300 border-slate-700 hover:border-emerald-500/40"
                    : "bg-slate-900 text-slate-500 border-slate-700"
              }`}
              data-testid="match-machines-btn"
              title={isProPlus ? t("marketplace.matchTooltip") : t("marketplace.matchProPlusRequired")}
            >
              <Cpu size={14}/> {t("marketplace.matchMyMachines")}
              {!isProPlus && <span className="text-[9px] label-tech px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/40 ml-1">PRO+</span>}
              {onlyCompatible && <span className="text-[10px] font-mono-data ml-1">ON</span>}
            </button>
          )}
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"/>
            <input type="text" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder={t("marketplace.search")} className="w-full pl-9 pr-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-sm" data-testid="mp-filter-search"/>
          </div>
          <select value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })} className="px-3 py-2 rounded-md text-sm bg-slate-900 border border-slate-700" data-testid="mp-filter-sort">
            {SORT_OPTIONS.map((s) => <option key={s.v} value={s.v}>{t("marketplace.sortBy")}: {s.label}</option>)}
          </select>
          <button onClick={() => setShowFilters((s) => !s)} className={`px-3 py-2 rounded-md text-sm border flex items-center gap-2 ${showFilters ? "bg-blue-500/10 text-blue-300 border-blue-500/50" : "bg-slate-900 text-slate-300 border-slate-700"}`} data-testid="mp-filter-toggle">
            <Filter size={14}/> {t("marketplace.filters")} {activeFilters > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-blue-500 text-white text-[10px] font-bold">{activeFilters}</span>}
          </button>
          {activeFilters > 0 && (
            <button onClick={() => setFilters(EMPTY_FILTERS)} className="px-3 py-2 rounded-md text-sm text-slate-400 hover:text-slate-100 flex items-center gap-1" data-testid="mp-filter-reset">
              <X size={14}/> {t("marketplace.filtersReset")}
            </button>
          )}
        </div>

        {compatibleError && (
          <div className="mb-3 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-200 text-xs flex items-center gap-2" data-testid="match-error">
            <Cpu size={12}/> {compatibleError}
          </div>
        )}

        {showFilters && (
          <div className="mb-5 p-4 rounded-md bg-slate-900 border border-slate-800 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="mp-filters-panel">
            <FilterField label={t("marketplace.country")}>
              <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} className="w-full px-2 py-1.5 rounded text-sm" data-testid="mp-filter-country">
                {COUNTRY_OPTIONS.map((c) => <option key={c.v || "all"} value={c.v}>{c.label}</option>)}
              </select>
            </FilterField>
            <FilterField label={t("marketplace.city")}>
              <input value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className="w-full px-2 py-1.5 rounded text-sm" placeholder={t("marketplace.cityPh")} data-testid="mp-filter-city"/>
            </FilterField>
            <FilterField label={t("marketplace.machineCategory")}>
              <select value={filters.machine_category} onChange={(e) => setFilters({ ...filters, machine_category: e.target.value })} className="w-full px-2 py-1.5 rounded text-sm" data-testid="mp-filter-machine">
                {MACHINE_OPTIONS.map((m) => <option key={m.v || "all"} value={m.v}>{m.label}</option>)}
              </select>
            </FilterField>
            <FilterField label={t("marketplace.tolerance")}>
              <select value={filters.tolerance_class} onChange={(e) => setFilters({ ...filters, tolerance_class: e.target.value })} className="w-full px-2 py-1.5 rounded text-sm" data-testid="mp-filter-tolerance">
                {TOLERANCE_OPTIONS.map((tl) => <option key={tl.v || "all"} value={tl.v}>{tl.label}</option>)}
              </select>
            </FilterField>
            <FilterField label={t("marketplace.material")}>
              <input value={filters.material} onChange={(e) => setFilters({ ...filters, material: e.target.value })} className="w-full px-2 py-1.5 rounded text-sm" placeholder="inox, alluminio..." data-testid="mp-filter-material"/>
            </FilterField>
            <FilterField label={t("marketplace.qtyMin")}>
              <input type="number" min="0" value={filters.qty_min} onChange={(e) => setFilters({ ...filters, qty_min: e.target.value })} className="w-full px-2 py-1.5 rounded text-sm font-mono-data" data-testid="mp-filter-qty-min"/>
            </FilterField>
            <FilterField label={t("marketplace.qtyMax")}>
              <input type="number" min="0" value={filters.qty_max} onChange={(e) => setFilters({ ...filters, qty_max: e.target.value })} className="w-full px-2 py-1.5 rounded text-sm font-mono-data" data-testid="mp-filter-qty-max"/>
            </FilterField>
            <FilterField label={t("marketplace.budgetMax")}>
              <input type="number" min="0" value={filters.budget_max} onChange={(e) => setFilters({ ...filters, budget_max: e.target.value })} className="w-full px-2 py-1.5 rounded text-sm font-mono-data" data-testid="mp-filter-budget-max"/>
            </FilterField>
            <FilterField label={t("marketplace.deadlineBefore")}>
              <input type="date" value={filters.deadline_before} onChange={(e) => setFilters({ ...filters, deadline_before: e.target.value })} className="w-full px-2 py-1.5 rounded text-sm font-mono-data" data-testid="mp-filter-deadline"/>
            </FilterField>
          </div>
        )}

        <div className="flex border-b border-slate-800 mb-4">
          <T active={tab === "open"} onClick={() => setTab("open")} label={`${t("marketplace.tabOpen")} (${rfqs.length})`} testid="tab-open"/>
          <T active={tab === "mine"} onClick={() => setTab("mine")} label={`${t("marketplace.tabMine")} (${mineRfqs.length})`} testid="tab-mine"/>
        </div>

        {selected ? (
          <RfqDetail rfq={selected} onClose={() => setSelected(null)} onSubmitOffer={submitOffer} offer={offer} setOffer={setOffer} onCloseRfq={() => { closeRfq(selected.id); setSelected(null); }} t={t}/>
        ) : list.length === 0 ? (
          <div className="tech-card p-16 text-center">
            <ShoppingCart size={28} className="mx-auto text-slate-500 mb-3"/>
            <div className="font-display font-medium text-slate-100">
              {activeFilters > 0 ? t("marketplace.noResultsFilter") : (tab === "open" ? t("marketplace.noResultsOpen") : t("marketplace.noResultsMine"))}
            </div>
            <div className="text-slate-500 text-sm mt-1">
              {activeFilters > 0 ? t("marketplace.noResultsHint") : (tab === "open" ? t("marketplace.openHint") : t("marketplace.mineHint"))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {list.map((r) => {
              const location = [r.location_city, r.location_country].filter(Boolean).join(", ");
              return (
              <button key={r.id} onClick={() => openRfq(r.id)} className="tech-card p-5 text-left hover:border-blue-500/60" data-testid={`rfq-${r.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="font-display font-semibold text-slate-100">{r.title}</div>
                  <span className={`text-[10px] label-tech px-2 py-1 rounded ${r.status === "open" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-slate-700 text-slate-400"}`}>{r.status}</span>
                </div>
                <div className="text-xs text-slate-500 mb-2">{r.workshop_name}</div>
                <p className="text-sm text-slate-300 line-clamp-2 mb-3">{r.description}</p>
                <div className="flex flex-wrap gap-2 text-xs text-slate-400">
                  <Chip icon={<Hash size={11}/>} text={`${r.quantity} pz`}/>
                  {r.material && <Chip text={r.material}/>}
                  {r.machine_category && <Chip icon={<Cpu size={11}/>} text={machineLabel(r.machine_category, t)}/>}
                  {r.tolerance_class && <Chip text={toleranceLabel(r.tolerance_class, t)}/>}
                  {location && <Chip icon={<MapPin size={11}/>} text={location}/>}
                  {r.deadline && <Chip icon={<Clock size={11}/>} text={r.deadline}/>}
                  {r.budget_max_eur && <Chip text={`€ ${r.budget_max_eur}`}/>}
                  <span className="ml-auto text-blue-400 font-mono-data">{r.offer_count} {t("marketplace.offersReceived").toLowerCase()}</span>
                </div>
              </button>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function machineLabel(v, t) {
  if (!v) return "";
  return t ? t(`marketplace.machineOpts.${v}`, { defaultValue: v }) : v;
}
function toleranceLabel(v, t) {
  if (!v) return "";
  return t ? t(`marketplace.toleranceOpts.${v}`, { defaultValue: v }) : v;
}

function Chip({ icon, text }) {
  return <span className="flex items-center gap-1 px-2 py-1 rounded bg-slate-900 border border-slate-800">{icon}{text}</span>;
}

function FilterField({ label, children }) {
  return (
    <div>
      <div className="label-tech text-[10px] mb-1">{label}</div>
      {children}
    </div>
  );
}

function T({ active, onClick, label, testid }) {
  return <button onClick={onClick} className={`px-4 py-2.5 text-sm border-b-2 ${active ? "border-blue-500 text-blue-400" : "border-transparent text-slate-400 hover:text-slate-200"}`} data-testid={testid}>{label}</button>;
}
function L({ children }) { return <label className="label-tech block mb-2">{children}</label>; }

function RfqDetail({ rfq, onClose, onSubmitOffer, offer, setOffer, onCloseRfq, t }) {
  const location = [rfq.location_city, rfq.location_country].filter(Boolean).join(", ");
  const operations = (rfq.operations || []).join(", ");
  return (
    <div className="tech-card p-6 fade-in-up" data-testid="rfq-detail">
      <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-200 mb-3">← {t("marketplace.backToList")}</button>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-tech mb-1">{t("marketplace.rfq")} · {rfq.id.slice(0,8)}</div>
          <h2 className="font-display font-bold text-2xl text-slate-100">{rfq.title}</h2>
          <div className="text-sm text-slate-500 mt-1">{rfq.workshop_name}</div>
        </div>
        {rfq.is_owner && rfq.status === "open" && (
          <button onClick={onCloseRfq} className="px-3 py-1.5 rounded text-xs bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20" data-testid="close-rfq-btn">{t("marketplace.closeRfq")}</button>
        )}
      </div>
      <p className="text-slate-300 whitespace-pre-wrap mb-4">{rfq.description}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-slate-400 mb-6">
        {rfq.material && <Info l={t("marketplace.material")} v={rfq.material}/>}
        {rfq.quantity && <Info l={t("marketplace.quantity")} v={`${rfq.quantity} pz`}/>}
        {rfq.machine_category && <Info l={t("marketplace.machineCategory")} v={machineLabel(rfq.machine_category, t)}/>}
        {rfq.tolerance_class && <Info l={t("marketplace.tolerance")} v={toleranceLabel(rfq.tolerance_class, t)}/>}
        {rfq.deadline && <Info l={t("marketplace.deadline")} v={rfq.deadline}/>}
        {(rfq.location_city || rfq.location_country) && <Info l={t("marketplace.place")} v={location}/>}
        {rfq.budget_max_eur && <Info l={t("marketplace.budget")} v={`€ ${rfq.budget_max_eur}`}/>}
        {(rfq.operations || []).length > 0 && <Info l={t("marketplace.operations").replace(" (csv)","")} v={operations}/>}
      </div>

      {!rfq.is_owner && rfq.status === "open" && (
        <form onSubmit={onSubmitOffer} className="bg-slate-900/60 border border-slate-700 rounded-md p-4 mb-6" data-testid="offer-form">
          <div className="font-display font-semibold text-slate-100 mb-3">{t("marketplace.yourOffer")}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><L>{t("marketplace.price")} *</L><input type="number" required value={offer.price_eur} onChange={(e) => setOffer({ ...offer, price_eur: e.target.value })} className="w-full px-3 py-2 rounded-md text-sm" data-testid="offer-price"/></div>
            <div><L>{t("marketplace.leadTime")} *</L><input type="number" required value={offer.lead_time_days} onChange={(e) => setOffer({ ...offer, lead_time_days: e.target.value })} className="w-full px-3 py-2 rounded-md text-sm" data-testid="offer-lead-time"/></div>
            <div><L>{t("common.notes")}</L><input value={offer.notes} onChange={(e) => setOffer({ ...offer, notes: e.target.value })} className="w-full px-3 py-2 rounded-md text-sm"/></div>
          </div>
          <button type="submit" className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2 mt-3" data-testid="submit-offer-btn"><Send size={12}/> {t("marketplace.submitOffer")}</button>
        </form>
      )}

      <div className="label-tech mb-2">{t("marketplace.offersReceived")} ({(rfq.offers || []).length})</div>
      {(rfq.offers || []).length === 0 ? <div className="text-sm text-slate-500 italic">{t("marketplace.noOffersYet")}</div> : (
        <div className="divide-y divide-slate-800">
          {rfq.offers.map((o) => (
            <div key={o.id} className="py-3 flex items-center gap-4">
              <div className="flex-1">
                <div className="text-sm text-slate-100">{o.workshop_name}</div>
                <div className="text-xs text-slate-500">{o.notes || "—"}</div>
              </div>
              <div className="text-right">
                <div className="font-mono-data text-blue-400 text-lg">€ {o.price_eur}</div>
                <div className="text-xs text-slate-500">{o.lead_time_days} gg</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Info({ l, v }) { return <div className="px-2 py-1.5 rounded bg-slate-900 border border-slate-800"><div className="label-tech text-[9px]">{l}</div><div className="text-slate-200">{v}</div></div>; }
